import {
    GetQuickQuoteQuery,
    GetQuickQuoteQueryVariables,
    ListQuickQuotesByPropertyQuery,
    ListQuickQuotesByPropertyQueryVariables,
    ListQuickQuotesBySolutionQuery,
    ListQuickQuotesBySolutionQueryVariables,
    QuickQuote,
    UpdateQuickQuoteMutation,
    UpdateQuickQuoteMutationVariables
} from "../../../API";
import {
    getQuickQuote,
    listQuickQuotesByProperty,
    listQuickQuotesBySolution
} from "../../../graphql/queries";

import { API } from "aws-amplify";
import { AuthIDTokenSupplierFactory } from "../../auth/AuthIDTokenSupplierFactory";
import ClientLogger from "../../logging/ClientLogger";
import ClientLoggerFactory from "../../logging/ClientLoggerFactory";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { IDTokenSupplier } from "../../auth/IDTokenSupplier";
import { QuickQuoteDAO } from "./QuickQuoteDAO";
import { updateQuickQuote } from "../../../graphql/mutations";

export class GraphQLQuickQuoteDAO implements QuickQuoteDAO {
    private readonly api: typeof API;
    private readonly idTokenSupplier: IDTokenSupplier;
    private readonly clientLogger: ClientLogger;

    constructor(
        api: typeof API,
        idTokenSupplier: IDTokenSupplier,
        clientLogger: ClientLogger
    ) {
        this.api = api;
        this.idTokenSupplier = idTokenSupplier;
        this.clientLogger = clientLogger;
    }

    public async get(id: string): Promise<QuickQuote | undefined> {
        const variables: GetQuickQuoteQueryVariables = { id };
        const authToken = await this.idTokenSupplier.get();
        const response = await this.api.graphql({
            query: getQuickQuote,
            variables,
            authToken
        }) as GraphQLResult<GetQuickQuoteQuery>;

        return response.data?.getQuickQuote ?? undefined;
    }

    public async getBySolutionId(solutionId: string): Promise<QuickQuote | undefined> {
        const variables: ListQuickQuotesBySolutionQueryVariables = { generatedSolutionId: solutionId };
        const authToken = await this.idTokenSupplier.get();
        const response = await this.api.graphql({
            query: listQuickQuotesBySolution,
            variables,
            authToken
        }) as GraphQLResult<ListQuickQuotesBySolutionQuery>;

        return response.data?.listQuickQuotesBySolution?.items[0] ?? undefined;
    }

    public async getByPropertyId(propertyId: string): Promise<QuickQuote[]> {
        const authToken = await this.idTokenSupplier.get();
        let variables: ListQuickQuotesByPropertyQueryVariables = { propertyId: propertyId };
        let allQuickQuotes: QuickQuote[] = [];

        do {
            const response = await this.api.graphql({
                query: listQuickQuotesByProperty,
                variables,
                authToken
            }) as GraphQLResult<ListQuickQuotesByPropertyQuery>;
            allQuickQuotes.push(...(response.data?.listQuickQuotesByProperty?.items?.filter(item => item !== null) as QuickQuote[] ?? []));
            variables.nextToken = response.data?.listQuickQuotesByProperty?.nextToken;
        } while (!!variables.nextToken);

        return allQuickQuotes;
    }

    public async update(quickQuote: QuickQuote): Promise<QuickQuote | undefined> {
        this.clientLogger.info(
            `Updating Quick Quote with id=${quickQuote.id}`,
            quickQuote,
            ["GraphQLQuickQuoteDAO.Update.Attempt"]
        );
        const variables: UpdateQuickQuoteMutationVariables = {
            input: {
                id: quickQuote.id,
                generatedProposalId: quickQuote.generatedProposalId,
                generatedSolutionId: quickQuote.generatedSolutionId,
                status: quickQuote.status,
                _version: quickQuote._version,
                issueIds: quickQuote.issueIds,
            }
        };
        const authToken = await this.idTokenSupplier.get();
        const response = await this.api.graphql({
            query: updateQuickQuote,
            variables,
            authToken
        }) as GraphQLResult<UpdateQuickQuoteMutation>;

        return response.data?.updateQuickQuote ?? undefined;
    }
}

export const graphQLQuickQuoteDAO = new GraphQLQuickQuoteDAO(API, AuthIDTokenSupplierFactory.getInstance(), ClientLoggerFactory.getClientLogger("GraphQLQuickQuoteDAO"));